import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Set up axios defaults
axios.defaults.headers.common['Content-Type'] = 'application/json';

// JWT Auto-Detection and Token Management
let isHandlingAuthFailure = false;
let isRefreshing = false;
let refreshSubscribers = [];

// Function to clear authentication data - EXPORTED for use in logout handlers
export const clearAuthData = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('deviceId');
  localStorage.removeItem('userType');  
  localStorage.removeItem('signInTime');
  localStorage.removeItem('tokenExpiresAt');
};

// Function to store authentication data
export const storeAuthData = (data) => {
  if (data.access_token) localStorage.setItem('token', data.access_token);
  if (data.refresh_token) localStorage.setItem('refreshToken', data.refresh_token);
  if (data.device_id) localStorage.setItem('deviceId', data.device_id);
  if (data.access_token_expires_in) {
    const expiresAt = Date.now() + (data.access_token_expires_in * 1000);
    localStorage.setItem('tokenExpiresAt', expiresAt.toString());
  }
};

// Function to check if token is expired
const isTokenExpired = (token) => {
  if (!token) return true;
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Date.now() / 1000;
    return payload.exp < currentTime;
  } catch (error) {
    console.warn('Error parsing JWT token:', error);
    return true;
  }
};

// Function to check if token needs refresh (within 5 minutes of expiry)
const tokenNeedsRefresh = () => {
  const token = localStorage.getItem('token');
  if (!token) return false;
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Date.now() / 1000;
    const fiveMinutesFromNow = currentTime + (5 * 60); // 5 minutes buffer
    return payload.exp < fiveMinutesFromNow;
  } catch (error) {
    return false;
  }
};

// Function to get the refresh endpoint based on user type
const getRefreshEndpoint = () => {
  const userType = localStorage.getItem('userType');
  switch (userType) {
    case 'super_admin':
      return `${API}/super-admin/token/refresh`;
    case 'admin':
      return `${API}/admin/token/refresh`;
    case 'user':
      return `${API}/token/refresh`;
    default:
      return null;
  }
};

// Function to add subscriber to wait for token refresh
const subscribeTokenRefresh = (callback) => {
  refreshSubscribers.push(callback);
};

// Function to notify all subscribers with new token
const onTokenRefreshed = (newToken) => {
  refreshSubscribers.forEach(callback => callback(newToken));
  refreshSubscribers = [];
};

// Function to refresh the token
const refreshToken = async () => {
  const refreshTokenValue = localStorage.getItem('refreshToken');
  const deviceId = localStorage.getItem('deviceId');
  const refreshEndpoint = getRefreshEndpoint();
  
  if (!refreshTokenValue || !deviceId || !refreshEndpoint) {
    throw new Error('Missing refresh credentials');
  }
  
  const response = await axios.post(refreshEndpoint, {
    refresh_token: refreshTokenValue,
    device_id: deviceId
  });
  
  // Store the new tokens
  storeAuthData(response.data);
  
  console.log('🔄 Token refreshed successfully');
  return response.data.access_token;
};

// Function to handle authentication failure with user-friendly messages
const handleAuthFailure = () => {
  if (isHandlingAuthFailure) return;
  
  isHandlingAuthFailure = true;
  clearAuthData();
  
  // Show user-friendly message only once
  console.warn('Session expired or invalid - redirecting to login');
  
  // Determine appropriate login page based on current location
  const path = window.location.pathname;
  let targetLogin = '/';
  
  if (path.includes('/super-admin')) {
    targetLogin = '/super-admin/login';
  } else if (path.includes('/admin')) {
    targetLogin = '/admin/login';
  }
  
  // Redirect to appropriate login page
  window.location.href = targetLogin;
  
  // Reset flag after redirect
  setTimeout(() => {
    isHandlingAuthFailure = false;
  }, 2000);
};

// Request interceptor to add token to headers
axios.interceptors.request.use(
  async (config) => {
    const token = localStorage.getItem('token');
    
    // Skip auth header for login and refresh endpoints
    if (config.url?.includes('/login') || config.url?.includes('/token/refresh') || config.url?.includes('/mobile-handoff')) {
      return config;
    }
    
    // Check if token needs refresh before making the request
    if (token && tokenNeedsRefresh() && !isRefreshing) {
      isRefreshing = true;
      
      try {
        const newToken = await refreshToken();
        config.headers.Authorization = `Bearer ${newToken}`;
        onTokenRefreshed(newToken);
      } catch (error) {
        console.error('Token refresh failed in request interceptor:', error);
        // Continue with old token, the response interceptor will handle 401
      } finally {
        isRefreshing = false;
      }
    } else if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle 401 with token refresh
axios.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // Track errors for admin users (Items 13 & 14)
    const userType = localStorage.getItem('userType');
    const token = localStorage.getItem('token');
    
    if (userType === 'admin' && token && error.response) {
      // Don't track errors from the tracking endpoint itself (prevent loops)
      if (!error.config?.url?.includes('/track-error') && !error.config?.url?.includes('/token/refresh')) {
        try {
          // Decode token to get session_id
          const payload = JSON.parse(atob(token.split('.')[1]));
          const sessionId = payload.session_id;
          
          // Determine error type
          const status = error.response.status;
          const errorType = status < 500 ? 'client_error' : 'server_error';
          
          // Track error (fire and forget - don't await)
          axios.post(`${API}/admin/track-error`, {
            session_id: sessionId,
            error_type: errorType,
            action_attempted: error.config?.action || 'unknown',
            endpoint: error.config?.url,
            http_method: error.config?.method?.toUpperCase(),
            error_message: error.response?.data?.detail || error.message,
            http_status: status
          }, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(() => {}); // Silent failure
        } catch (trackError) {
          // Silent failure - don't break user experience
          console.debug('Error tracking failed:', trackError);
        }
      }
    }
    
    // Handle 401 Unauthorized responses
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Skip refresh for login endpoints
      if (originalRequest.url?.includes('/login')) {
        return Promise.reject(error);
      }
      
      // Skip refresh for refresh endpoints (avoid infinite loop)
      if (originalRequest.url?.includes('/token/refresh')) {
        console.error('Refresh token expired or invalid - redirecting to login');
        handleAuthFailure();
        return Promise.reject(error);
      }
      
      originalRequest._retry = true;
      
      // If already refreshing, wait for it to complete
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh((newToken) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(axios(originalRequest));
          });
        });
      }
      
      isRefreshing = true;
      
      try {
        const newToken = await refreshToken();
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        onTokenRefreshed(newToken);
        return axios(originalRequest);
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        handleAuthFailure();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    
    return Promise.reject(error);
  }
);

// Proactive token refresh - set up a timer to refresh before expiry
export const setupTokenRefreshTimer = () => {
  const tokenExpiresAt = localStorage.getItem('tokenExpiresAt');
  if (!tokenExpiresAt) return;
  
  const expiresAt = parseInt(tokenExpiresAt, 10);
  const now = Date.now();
  const refreshBuffer = 5 * 60 * 1000; // 5 minutes before expiry
  const timeUntilRefresh = expiresAt - now - refreshBuffer;
  
  if (timeUntilRefresh > 0) {
    console.log(`🕐 Token refresh scheduled in ${Math.round(timeUntilRefresh / 1000 / 60)} minutes`);
    setTimeout(async () => {
      try {
        await refreshToken();
        setupTokenRefreshTimer(); // Set up next refresh
      } catch (error) {
        console.error('Scheduled token refresh failed:', error);
      }
    }, timeUntilRefresh);
  }
};

// Common fetch functions
export const fetchUsers = async () => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.get(`${API}/admin/users`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  } catch (error) {
    console.error('Failed to fetch users:', error);
    throw error;
  }
};

export const fetchSites = async (includeArchived = false) => {
  try {
    const response = await axios.get(`${API}/admin/sites`, {
      params: { include_archived: includeArchived }
    });
    return response.data;
  } catch (error) {
    console.error('Failed to fetch sites:', error);
    throw error;
  }
};

export const fetchTasks = async () => {
  try {
    const response = await axios.get(`${API}/admin/tasks`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch tasks:', error);
    throw error;
  }
};

export const fetchRoles = async () => {
  try {
    const response = await axios.get(`${API}/roles`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch roles:', error);
    throw error;
  }
};

export const fetchDepartments = async () => {
  try {
    const response = await axios.get(`${API}/departments`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch departments:', error);
    throw error;
  }
};

// Task utilities
export const getStatusColor = (status) => {
  const colors = {
    pending: 'bg-yellow-100 border-yellow-300 text-yellow-800',
    in_progress: 'bg-blue-100 border-blue-300 text-blue-800',
    completed: 'bg-green-100 border-green-300 text-green-800',
    cancelled: 'bg-red-100 border-red-300 text-red-800'
  };
  return colors[status] || 'bg-gray-100 border-gray-300 text-gray-800';
};

export const getPriorityColor = (priority) => {
  const colors = {
    low: 'border-l-gray-400',
    medium: 'border-l-blue-400',
    high: 'border-l-orange-400',
    urgent: 'border-l-red-400'
  };
  return colors[priority] || 'border-l-gray-400';
};

export default axios;